/* AgentMeeting — Room variation C: Split-stance arena (red/blue lanes)
 *
 * Wired to the live backend via `useMeetingStream` + `meetings.get/messages`.
 */
import React from 'react';
import { AgentT, MODELS, ModelId, STANCE_COLOR } from '../shared/data';
import {
  Avatar, StanceTag, ModelChip, RoundSteps, CostMeter, RoleSwitcher,
  renderInline, I, Role,
} from '../shared/ui';
import { meetings as meetingsApi, HTTPError, type ServerEvent } from '../shared/api';
import { useMeetingStream } from '../shared/useMeetingStream';
import { useAuth } from '../shared/auth';
import { useI18n } from '../shared/i18n';

type Props = { role: Role; meetingType: string; embedded?: boolean; topBar?: React.ReactNode };

interface ServerMsg {
  id: string;
  round_no: number;
  participant_id: string;
  kind: string;
  content: string;
  tokens_in?: number;
  tokens_out?: number;
  cost_cents?: number;
  parent_id?: string | null;
  created_at?: string;
  speaker_name?: string;
  speaker_short?: string;
  speaker_stance?: string;
  speaker_model?: string;
  _streaming?: boolean;
}

function synthAgent(m: ServerMsg): AgentT {
  const stance = (m.speaker_stance || 'neutral') as AgentT['stance'];
  return {
    id: m.participant_id || m.id,
    name: m.speaker_name || '—',
    short: m.speaker_short || '··',
    stance,
    stanceLabel: '',
    model: (m.speaker_model || null) as AgentT['model'],
    persona: '',
  };
}

function fmtTs(iso?: string): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-GB', { hour12: false }); }
  catch { return ''; }
}

function goMeetings() {
  if (typeof window !== 'undefined') location.hash = '#/app/meetings';
}

// Lane mapping per spec:
//   pro|finance → pro lane
//   con|risk|neutral → con lane
//   judge → judge bar (rendered separately, not in lanes)
const PRO_STANCES = new Set(['pro', 'finance']);
const CON_STANCES = new Set(['con', 'risk', 'neutral']);
const isProLeaning = (s?: string) => PRO_STANCES.has((s || 'neutral'));
const isConLeaning = (s?: string) => CON_STANCES.has((s || 'neutral'));

export function RoomArena({ role, meetingType, embedded, topBar }: Props) {
  useAuth();
  const { t } = useI18n();
  const meetingId = typeof window !== 'undefined'
    ? localStorage.getItem('agentmeeting.currentMeetingId')
    : null;

  const [serverMessages, setServerMessages] = React.useState<ServerMsg[] | null>(null);
  const [meetingMeta, setMeetingMeta] = React.useState<any>(null);
  const [loadErr, setLoadErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!meetingId) return;
    meetingsApi.get(meetingId)
      .then(setMeetingMeta)
      .catch((e: unknown) => setLoadErr(e instanceof HTTPError ? `meeting ${e.status}` : `${e}`));
    meetingsApi.messages(meetingId)
      .then(r => setServerMessages((r.messages || []) as ServerMsg[]))
      .catch((e: unknown) => setLoadErr(e instanceof HTTPError ? `messages ${e.status}` : `${e}`));
  }, [meetingId]);

  const streamOpts = {
    onTurnToken: (ev: ServerEvent) => {
      const { message_id, delta } = ev.payload || {};
      if (!message_id) return;
      setServerMessages(prev => (prev || []).map(m =>
        m.id === message_id ? { ...m, content: (m.content || '') + (delta || '') } : m
      ));
    },
    onTurnEnd: (ev: ServerEvent) => {
      const p = ev.payload || {};
      setServerMessages(prev => (prev || []).map(m =>
        m.id === p.message_id ? {
          ...m,
          content: p.content ?? m.content,
          tokens_in: p.tokens_in ?? m.tokens_in,
          tokens_out: p.tokens_out ?? m.tokens_out,
          cost_cents: p.cost_cents ?? m.cost_cents,
          _streaming: false,
        } : m
      ));
    },
    onMeetingState: (_ev: ServerEvent) => { /* banner could be added later */ },
    onAgendaAdvance: (ev: ServerEvent) => {
      setMeetingMeta((prev: any) => ({ ...(prev || {}), current_round: ev.payload?.round_no }));
    },
  };
  const { events, connected } = useMeetingStream(meetingId, streamOpts as any);

  const lastSeenIdx = React.useRef(0);
  React.useEffect(() => {
    for (let i = lastSeenIdx.current; i < events.length; i++) {
      const ev = events[i];
      if (ev.type === 'turn.start') {
        const p = ev.payload || {};
        if (!p.message_id) continue;
        setServerMessages(prev => {
          const list = prev || [];
          if (list.some(m => m.id === p.message_id)) return list;
          return [...list, {
            id: p.message_id,
            round_no: p.round_no ?? 0,
            participant_id: p.speaker_id || '',
            speaker_name: p.speaker_name,
            speaker_short: p.speaker_short,
            speaker_stance: p.speaker_stance,
            speaker_model: p.speaker_model,
            kind: 'speech',
            content: '',
            _streaming: true,
            created_at: new Date().toISOString(),
          }];
        });
      }
    }
    lastSeenIdx.current = events.length;
  }, [events]);

  const onPause = async () => {
    if (!meetingId) return;
    try { await meetingsApi.pause(meetingId); } catch (e) { setLoadErr(`${e}`); }
  };

  if (!meetingId) {
    return (
      <div className="frame room-frame col" style={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        {topBar}
        <div className="col gap-3" style={{
          maxWidth: 420, padding: 24, border: '1px solid var(--line-2)',
          borderRadius: 6, background: 'var(--bg-1)', textAlign: 'center',
        }}>
          <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>NO MEETING SELECTED</div>
          <div style={{ fontSize: 15 }}>Pick a meeting to enter the arena.</div>
          <button className="btn primary" onClick={goMeetings} title={t('app.meetings.my')}>Open meetings →</button>
        </div>
      </div>
    );
  }

  if (serverMessages == null) {
    return (
      <div className="frame room-frame col" style={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        {topBar}
        <div className="mono tiny dim">loading transcript…</div>
        {loadErr && <div className="mono tiny" style={{ color: 'var(--err)', marginTop: 8 }}>{loadErr}</div>}
      </div>
    );
  }

  // Build pro/con/judge agent rosters from message stances we've seen.
  const seen = new Map<string, AgentT>();
  for (const m of serverMessages) {
    const a = synthAgent(m);
    if (!seen.has(a.short)) seen.set(a.short, a);
  }
  const allAgents = Array.from(seen.values());
  const proLane  = allAgents.filter(a => isProLeaning(a.stance));
  const conLane  = allAgents.filter(a => isConLeaning(a.stance));
  const judges   = allAgents.filter(a => a.stance === 'judge');
  const judge    = judges[0] || null;

  const liveMsg = serverMessages.find(m => m._streaming);
  const liveAgent = liveMsg ? synthAgent(liveMsg) : null;

  const proMessages = serverMessages.filter(m => isProLeaning(m.speaker_stance));
  const conMessages = serverMessages.filter(m => isConLeaning(m.speaker_stance));

  const currentRound = meetingMeta?.current_round ?? 0;
  const totalRounds = meetingMeta?.total_rounds ?? Math.max(currentRound + 1, 6);

  return (
    <div className="frame room-frame" style={{ height: '100%' }}>
      {topBar}
      {!embedded && (
        <div className="term-bar">
          <div className="lights"><span /><span /><span /></div>
          <div className="crumbs">
            <span>~/meetings/{meetingMeta?.title || meetingId.slice(0, 8)}</span>
            <span className="sep">/</span>
            <span className="here">arena</span>
          </div>
          <div className="right">
            <CostMeter
              tokens={Math.round((meetingMeta?.spent_cents || 0) * 40)}
              cents={meetingMeta?.spent_cents || 0}
              budget={meetingMeta?.budget_cents || 500}
            />
            <RoleSwitcher role={role} onChange={() => {}} compact />
          </div>
        </div>
      )}

      <div className="flex center" style={{ padding: '14px 22px 12px', gap: 18, borderBottom: '1px solid var(--line-1)' }}>
        <div className="col gap-1 grow">
          <div className="meta">[{(meetingMeta?.type || meetingType).toUpperCase()}] · stance-pair · 红蓝对抗</div>
          <div style={{ fontSize: 16, fontWeight: 500 }}>
            {meetingMeta?.topic || meetingMeta?.title || '(no topic)'}
          </div>
        </div>
        <RoundSteps current={currentRound} total={totalRounds} />
        <div className="flex gap-1 center">
          <span className={'dot ' + (connected ? 'live pulse' : '')} />
          <span className="mono tiny live-fg">{connected ? 'LIVE' : 'OFFLINE'}</span>
        </div>
      </div>

      <div className="flex center" style={{
        padding: '10px 22px', gap: 12,
        background: 'var(--bg-1)', borderBottom: '1px solid var(--line-1)',
      }}>
        <span className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>JUDGE</span>
        {judge ? (
          <>
            <Avatar agent={judge} size="sm" />
            <span className="sm">{judge.name}</span>
            {judge.model && <ModelChip modelId={judge.model} />}
          </>
        ) : (
          <span className="meta dim">(no judge in this meeting)</span>
        )}
        <span className="ascii-sep" style={{ flex: 1, padding: 0 }}>—————————————————————————————</span>
        <span className="mono tiny dim">
          {loadErr ? <span style={{ color: 'var(--err)' }}>{loadErr}</span> : 'verdict pending'}
        </span>
      </div>

      <div className="grow flex room-arena-body" style={{ minHeight: 0 }}>
        <Lane
          side="pro" title={t('app.studio.stance.pro')} color="var(--pro)"
          agents={proLane}
          messages={proMessages}
          live={liveAgent && isProLeaning(liveAgent.stance) ? liveAgent : undefined}
        />
        <div className="arena-divider" style={{ width: 1, background: 'var(--line-1)', position: 'relative' }}>
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: 'var(--bg-0)', padding: '8px 6px', borderRadius: 4,
          }}>
            <div className="mono tiny dim" style={{ writingMode: 'vertical-rl', letterSpacing: '0.2em' }}>VS</div>
          </div>
        </div>
        <Lane
          side="con" title={t('app.studio.stance.con')} color="var(--err)"
          agents={conLane}
          messages={conMessages}
          live={liveAgent && isConLeaning(liveAgent.stance) ? liveAgent : undefined}
        />
      </div>

      <VoteBar role={role} onPause={onPause} />
    </div>
  );
}

function Lane({
  title, color, agents, messages, live,
}: {
  side: 'pro' | 'con';
  title: string;
  color: string;
  agents: AgentT[];
  messages: ServerMsg[];
  live?: AgentT;
}) {
  return (
    <div className="col grow" style={{ minWidth: 0 }}>
      <div className="flex center" style={{
        padding: '10px 16px', gap: 10,
        borderBottom: '1px solid var(--line-1)',
        background: 'linear-gradient(90deg, ' + color + '12, transparent)',
      }}>
        <span className="dot" style={{ background: color }} />
        <span className="mono sm" style={{ color, letterSpacing: '0.06em', fontWeight: 600 }}>{title.toUpperCase()}</span>
        <span className="dim mono tiny">· {agents.length} speakers</span>
        <span style={{ marginLeft: 'auto' }} className="mono tiny dim">{messages.length} turns</span>
      </div>

      <div className="flex center wrap gap-2" style={{ padding: '10px 16px', borderBottom: '1px solid var(--line-1)' }}>
        {agents.map(a => (
          <div key={a.id} className="flex center gap-2" style={{
            padding: '4px 8px 4px 4px', border: '1px solid var(--line-2)', borderRadius: 4,
            background: live && live.short === a.short ? 'var(--bg-2)' : 'transparent',
          }}>
            <Avatar agent={a} size="sm" />
            <div className="col">
              <span className="sm" style={{ lineHeight: 1.1 }}>{a.name.split('·')[0].trim()}</span>
              <span className="mono tiny dim">
                {a.model && MODELS[a.model as ModelId] ? MODELS[a.model as ModelId].short : (a.model || '—')}
              </span>
            </div>
            {live && live.short === a.short && <span className="dot live pulse" />}
          </div>
        ))}
        {agents.length === 0 && (
          <div className="meta dim">no speakers in this lane yet</div>
        )}
      </div>

      <div className="grow scroll col gap-3" style={{ padding: '14px 16px' }}>
        {messages.map(m => <ArenaTurn key={m.id} msg={m} color={color} />)}
        {messages.length === 0 && (
          <div className="meta dim" style={{ padding: 24, textAlign: 'center' }}>
            (no turns in this lane yet)
          </div>
        )}
      </div>
    </div>
  );
}

function ArenaTurn({ msg, color }: { msg: ServerMsg; color: string }) {
  const a = synthAgent(msg);
  const c = STANCE_COLOR[a.stance] || color;
  return (
    <div className="col gap-1" style={{ paddingLeft: 12, borderLeft: '2px solid ' + c }}>
      <div className="flex center gap-2 wrap">
        <span className="sm" style={{ fontWeight: 500 }}>{a.name.split('·')[0].trim()}</span>
        <span className="tag" style={{ borderColor: 'transparent', color: c, padding: '2px 0' }}>
          <span className="dot" style={{ background: c }} />
          <span style={{ letterSpacing: '0.04em' }}>{a.stance}</span>
        </span>
        <span className="dim">·</span>
        <span className="mono tiny dim">r{msg.round_no + 1} · {fmtTs(msg.created_at)}</span>
        <span className="dim" style={{ marginLeft: 'auto' }}>
          {a.model && <ModelChip modelId={a.model} withCost costCents={msg.cost_cents || 0} />}
        </span>
      </div>
      <div className="sm" style={{ lineHeight: 1.55, color: 'var(--fg-1)' }}>
        {renderInline(msg.content || '')}{msg._streaming && <span className="caret" />}
      </div>
      {msg.parent_id && <div className="mono tiny dim">↪ replying to {msg.parent_id.slice(0, 6)}</div>}
    </div>
  );
}

function VoteBar({ role, onPause }: { role: Role; onPause: () => void }) {
  const { t } = useI18n();
  return (
    <div className="flex room-vote-bar" style={{
      padding: '12px 16px', gap: 14, alignItems: 'center',
      borderTop: '1px solid var(--line-1)', background: 'var(--bg-1)',
    }}>
      <div className="col">
        <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.resolution.vote')}</div>
        <div className="mono sm dim">no votes yet — coming next wave</div>
      </div>

      <div className="flex grow" style={{
        height: 14, borderRadius: 2, overflow: 'hidden', border: '1px solid var(--line-2)',
      }}>
        <div style={{ flex: 1, background: 'var(--fg-3)' }} />
      </div>

      {role === 'host' && <button className="btn primary" disabled title="vote — coming next wave">{I.vote} {t('app.room.openVote')}</button>}
      {role === 'director' && (
        <>
          <button className="btn" onClick={onPause}>{I.pause} {t('app.room.director.pause')}</button>
          <button className="btn" disabled title="fork — coming next wave">{I.fork} {t('app.room.director.fork')}</button>
        </>
      )}
    </div>
  );
}
